<div class="inventory-dashboard">
  <!-- Header Section -->
  <div class="row mb-4">
    <div class="col-md-6">
      <h4>Inventory Dashboard</h4>
      <p class="text-muted">Monitor stock levels and manage inventory alerts</p>
    </div>
    <div class="col-md-6 text-end">
      <button class="btn btn-success me-2" (click)="openBulkUpdateModal()">
        <nb-icon icon="layers-outline"></nb-icon>
        Bulk Update
      </button>
    </div>
  </div>

  <!-- Summary Cards -->
  <div class="row mb-4" *ngIf="inventoryAnalytics">
    <div class="col-md-3">
      <nb-card>
        <nb-card-body class="text-center">
          <h6 class="text-muted">Total Products</h6>
          <h4 class="text-primary">{{ inventoryAnalytics.totalProducts || 0 }}</h4>
          <small class="text-muted">Active inventory items</small>
        </nb-card-body>
      </nb-card>
    </div>
    <div class="col-md-3">
      <nb-card>
        <nb-card-body class="text-center">
          <h6 class="text-muted">Low Stock Items</h6>
          <h4 class="text-warning">{{ inventoryAnalytics.lowStockCount || 0 }}</h4>
          <small class="text-muted">Below threshold</small>
        </nb-card-body>
      </nb-card>
    </div>
    <div class="col-md-3">
      <nb-card>
        <nb-card-body class="text-center">
          <h6 class="text-muted">Out of Stock</h6>
          <h4 class="text-danger">{{ inventoryAnalytics.outOfStockCount || 0 }}</h4>
          <small class="text-muted">Needs immediate attention</small>
        </nb-card-body>
      </nb-card>
    </div>
    <div class="col-md-3">
      <nb-card>
        <nb-card-body class="text-center">
          <h6 class="text-muted">Inventory Value</h6>
          <h4 class="text-success">{{ formatCurrency(inventoryAnalytics.totalValue || 0) }}</h4>
          <small class="text-muted">Total stock value</small>
        </nb-card-body>
      </nb-card>
    </div>
  </div>

  <!-- Search and Filters -->
  <nb-card class="mb-4">
    <nb-card-body>
      <div class="row align-items-center">
        <!-- Severity Filter -->
        <div class="col-md-3">
          <label class="form-label">Alert Severity</label>
          <select class="form-select" [(ngModel)]="selectedSeverity" (change)="onFilterChange()">
            <option *ngFor="let option of severityOptions" [value]="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Category Filter -->
        <div class="col-md-3">
          <label class="form-label">Category</label>
          <select class="form-select" [(ngModel)]="selectedCategory" (change)="onFilterChange()">
            <option value="">All Categories</option>
            <option *ngFor="let category of categories" [value]="category._id">
              {{ category.name }}
            </option>
          </select>
        </div>

        <!-- Sort -->
        <div class="col-md-2">
          <label class="form-label">Sort By</label>
          <select class="form-select" [(ngModel)]="sortBy" (change)="onSortChange()">
            <option value="severity">Severity</option>
            <option value="quantity">Stock Level</option>
            <option value="threshold">Threshold</option>
            <option value="name">Product Name</option>
          </select>
        </div>

        <div class="col-md-2">
          <label class="form-label">Order</label>
          <select class="form-select" [(ngModel)]="sortOrder" (change)="onSortChange()">
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>

        <!-- Actions -->
        <div class="col-md-2 d-flex align-items-end">
          <button class="btn btn-outline-secondary w-100" (click)="clearFilters()">
            Clear Filters
          </button>
        </div>
      </div>
    </nb-card-body>
  </nb-card>

  <!-- Bulk Actions -->
  <div *ngIf="selectedProducts.size > 0" class="bulk-actions mb-3">
    <nb-card>
      <nb-card-body class="py-2">
        <div class="row align-items-center">
          <div class="col-md-6">
            <span class="me-3">{{ selectedProducts.size }} products selected</span>
          </div>
          <div class="col-md-6">
            <div class="row">
              <div class="col-md-8">
                <select class="form-select" [(ngModel)]="bulkAction">
                  <option value="">Select Action</option>
                  <option value="restock">Quick Restock (+50)</option>
                  <option value="update_threshold">Update Thresholds</option>
                  <option value="generate_suggestions">Generate Suggestions</option>
                </select>
              </div>
              <div class="col-md-4">
                <button class="btn btn-primary w-100" (click)="openBulkUpdateModal()">
                  Execute
                </button>
              </div>
            </div>
          </div>
        </div>
      </nb-card-body>
    </nb-card>
  </div>

  <!-- Low Stock Alerts Table -->
  <nb-card>
    <nb-card-header>
      <h6 class="mb-0">Low Stock Alerts</h6>
      <span class="text-muted">{{ totalAlerts }} total alerts</span>
    </nb-card-header>
    <nb-card-body class="p-0">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th style="width: 40px;">
                <nb-checkbox 
                  [checked]="selectedProducts.size === lowStockAlerts.length && lowStockAlerts.length > 0"
                  [indeterminate]="selectedProducts.size > 0 && selectedProducts.size < lowStockAlerts.length"
                  (checkedChange)="selectAllProducts()">
                </nb-checkbox>
              </th>
              <th>Product</th>
              <th>Category</th>
              <th>Current Stock</th>
              <th>Threshold</th>
              <th>Severity</th>
              <th>Recommended Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let alert of lowStockAlerts; trackBy: trackByAlertId" 
                [class.table-danger]="alert.alertInfo?.severity === 'critical'"
                [class.table-warning]="alert.alertInfo?.severity === 'warning'">
              <td>
                <nb-checkbox 
                  [checked]="selectedProducts.has(alert._id)"
                  (checkedChange)="toggleProductSelection(alert._id)">
                </nb-checkbox>
              </td>
              
              <td>
                <div class="product-info">
                  <strong>{{ alert.name }}</strong>
                  <br>
                  <small class="text-muted">SKU: {{ alert.sku }}</small>
                </div>
              </td>
              
              <td>
                <span>{{ alert.category?.name || 'Uncategorized' }}</span>
              </td>
              
              <td>
                <div class="stock-info">
                  <span class="stock-quantity" 
                        [class.text-danger]="alert.inventory.quantity === 0"
                        [class.text-warning]="alert.inventory.quantity > 0 && alert.inventory.quantity <= alert.inventory.lowStockThreshold">
                    {{ alert.inventory.quantity }}
                  </span>
                  <br>
                  <small class="text-muted">
                    {{ alert.alertInfo?.percentageOfThreshold || 0 }}% of threshold
                  </small>
                </div>
              </td>
              
              <td>
                <span class="threshold-value">{{ alert.inventory.lowStockThreshold }}</span>
              </td>
              
              <td>
                <nb-badge 
                  [status]="getSeverityColor(alert.alertInfo?.severity)"
                  [text]="(alert.alertInfo?.severity || 'unknown') | titlecase">
                </nb-badge>
                <br>
                <small class="text-muted" *ngIf="alert.alertInfo?.daysUntilOut">
                  {{ alert.alertInfo.daysUntilOut }} days until out
                </small>
              </td>
              
              <td>
                <div class="reorder-info">
                  <strong>{{ alert.alertInfo?.recommendedReorder || 0 }}</strong>
                  <br>
                  <small class="text-muted">
                    Est. {{ formatCurrency((alert.alertInfo?.recommendedReorder || 0) * alert.price) }}
                  </small>
                </div>
              </td>
              
              <td>
                <div class="action-buttons">
                  <button 
                    class="btn btn-sm btn-success me-1" 
                    (click)="quickRestock(alert)"
                    nbTooltip="Quick Restock">
                    <nb-icon icon="plus-outline"></nb-icon>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Loading State -->
      <div *ngIf="loading" class="text-center p-4">
        <nb-spinner size="large"></nb-spinner>
        <p class="mt-2">Loading inventory alerts...</p>
      </div>

      <!-- Empty State -->
      <div *ngIf="!loading && lowStockAlerts.length === 0" class="text-center p-4">
        <nb-icon icon="checkmark-circle-outline" class="empty-icon mb-3 text-success"></nb-icon>
        <h5>All Stock Levels Good!</h5>
        <p class="text-muted">No low stock alerts at the moment.</p>
      </div>

      <!-- Pagination -->
      <div *ngIf="totalPages > 1" class="d-flex justify-content-between align-items-center p-3 border-top">
        <div>
          <span class="text-muted">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalAlerts) }} 
            of {{ totalAlerts }} alerts
          </span>
        </div>
        <div>
          <button 
            class="btn btn-outline-secondary me-2" 
            [disabled]="currentPage === 1"
            (click)="onPageChange(currentPage - 1)">
            Previous
          </button>
          <span class="me-2">Page {{ currentPage }} of {{ totalPages }}</span>
          <button 
            class="btn btn-outline-secondary" 
            [disabled]="currentPage === totalPages"
            (click)="onPageChange(currentPage + 1)">
            Next
          </button>
        </div>
      </div>
    </nb-card-body>
  </nb-card>
</div>

<!-- Bulk Update Modal -->
<nb-card *ngIf="showBulkUpdateModal" class="modal-overlay">
  <nb-card-header class="d-flex justify-content-between align-items-center">
    <h5>Bulk Inventory Update</h5>
    <button class="btn btn-ghost" (click)="closeBulkUpdateModal()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>
  <nb-card-body class="modal-body">
    <form [formGroup]="bulkUpdateForm" (ngSubmit)="executeBulkUpdate()">
      <div class="mb-3">
        <label class="form-label">Operation Type *</label>
        <select class="form-control" formControlName="operation" required>
          <option *ngFor="let op of bulkOperations" [value]="op.value">
            {{ op.label }}
          </option>
        </select>
      </div>

      <!-- Adjust Quantity Options -->
      <div *ngIf="bulkUpdateForm.get('operation')?.value === 'adjust_quantity'" class="mb-3">
        <div class="row">
          <div class="col-md-6">
            <label class="form-label">Adjustment Type</label>
            <select class="form-control" formControlName="adjustmentType">
              <option value="add">Add to Current Stock</option>
              <option value="subtract">Subtract from Current Stock</option>
              <option value="set">Set Absolute Value</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">Adjustment Value *</label>
            <input type="number" class="form-control" formControlName="adjustmentValue" 
                   min="1" required>
            <div *ngIf="bulkUpdateForm.get('adjustmentValue')?.invalid && bulkUpdateForm.get('adjustmentValue')?.touched" class="text-danger">
              Valid adjustment value is required
            </div>
          </div>
        </div>
      </div>

      <!-- Update Thresholds Options -->
      <div *ngIf="bulkUpdateForm.get('operation')?.value === 'update_thresholds'" class="mb-3">
        <div class="row">
          <div class="col-md-6">
            <label class="form-label">Threshold Type</label>
            <select class="form-control" formControlName="thresholdType">
              <option value="fixed">Fixed Value</option>
              <option value="percentage">Percentage of Current Stock</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">New Threshold *</label>
            <input type="number" class="form-control" formControlName="newThreshold" 
                   min="0" required>
            <small class="text-muted">
              <span *ngIf="bulkUpdateForm.get('thresholdType')?.value === 'fixed'">Units</span>
              <span *ngIf="bulkUpdateForm.get('thresholdType')?.value === 'percentage'">Percentage (%)</span>
            </small>
          </div>
        </div>
      </div>

      <!-- Selected Products Info -->
      <div class="mb-3">
        <div class="alert alert-info">
          <nb-icon icon="info-outline"></nb-icon>
          <span *ngIf="bulkUpdateForm.get('operation')?.value !== 'restock_suggestions'">
            This operation will affect {{ selectedProducts.size }} selected products.
          </span>
          <span *ngIf="bulkUpdateForm.get('operation')?.value === 'restock_suggestions'">
            This will generate restock suggestions for all low stock products.
          </span>
        </div>
      </div>

      <div class="d-flex justify-content-end">
        <button type="button" class="btn btn-outline-secondary me-2" (click)="closeBulkUpdateModal()">
          Cancel
        </button>
        <button type="submit" class="btn btn-primary" [disabled]="bulkUpdateForm.invalid">
          Execute Update
        </button>
      </div>
    </form>
  </nb-card-body>
</nb-card>

<!-- Restock Suggestions Modal -->
<nb-card *ngIf="showRestockModal" class="modal-overlay large-modal">
  <nb-card-header class="d-flex justify-content-between align-items-center">
    <h5>Restock Suggestions</h5>
    <button class="btn btn-ghost" (click)="closeRestockModal()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>
  <nb-card-body class="modal-body">
    <div class="mb-3">
      <div class="alert alert-success">
        <nb-icon icon="checkmark-circle-outline"></nb-icon>
        Generated {{ restockSuggestions.length }} restock suggestions based on current stock levels and sales trends.
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-sm">
        <thead>
          <tr>
            <th>Product</th>
            <th>Current Stock</th>
            <th>Threshold</th>
            <th>Suggested Order</th>
            <th>Estimated Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let suggestion of restockSuggestions">
            <td>
              <div>
                <strong>{{ suggestion.name }}</strong>
                <br>
                <small class="text-muted">{{ suggestion.sku }}</small>
              </div>
            </td>
            <td>{{ suggestion.currentStock }}</td>
            <td>{{ suggestion.threshold }}</td>
            <td><strong>{{ suggestion.suggestedReorder }}</strong></td>
            <td>{{ formatCurrency(suggestion.estimatedCost) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="row mt-3">
      <div class="col-md-6">
        <div class="summary-card">
          <h6>Summary</h6>
          <p><strong>Total Products:</strong> {{ restockSuggestions.length }}</p>
          <p><strong>Total Estimated Cost:</strong> 
            {{ formatCurrency(getTotalRestockCost()) }}
          </p>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end mt-3">
      <button type="button" class="btn btn-outline-secondary me-2" (click)="closeRestockModal()">
        Cancel
      </button>
      <button type="button" class="btn btn-success" (click)="applyRestockSuggestions()">
        Apply All Suggestions
      </button>
    </div>
  </nb-card-body>
</nb-card>